import { useEffect, useState } from "react";
import axios from "axios";
import {
  RadioTower, RefreshCw, Apple as AppleIcon, Wallet, AlertTriangle,
  CheckCircle2, ChevronRight, Eye, ShieldAlert, ShieldCheck,
} from "lucide-react";
import { API } from "../../config";

// Toast/sonner-free implementation — mirrors the other admin widgets that
// use plain inline status text instead of toast notifications.

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const COUNTRIES = [
  "", "IT", "FR", "ES", "DE", "NL", "PT", "PL", "SE", "NO", "FI", "DK",
  "GR", "UK", "BE", "CH", "CZ", "IRL", "GBZ",
];

/** FastAPI returns 422 errors as ``{detail:[{type,loc,msg,...}]}``.
 * Rendering that array directly via `{error}` crashes React with
 * "Objects are not valid as a React child". Normalize to a plain
 * string for every shape (string | object | array of objects). */
const extractErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : (d?.msg || JSON.stringify(d))))
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }
  return detail || err?.message || fallback;
};

export const WalletMassRefreshWidget = () => {
  // form state
  const [platform, setPlatform] = useState("both");
  const [country, setCountry] = useState("");
  const [reason, setReason] = useState("");
  const [batchSleepMs, setBatchSleepMs] = useState(50);
  // Fabrizio CR 26/06/2026 — per-run push notify (overrides env var).
  const [notifyPush, setNotifyPush] = useState(false);

  // preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  // run state
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  // Reset preview whenever filter changes — stale count is misleading
  useEffect(() => {
    setPreview(null);
  }, [platform, country]);

  // Auto-load preview on widget mount so the operator immediately sees
  // the blast radius AND the Apple credentials status. Without this,
  // the warning banner only appears AFTER clicking "Anteprima target",
  // which is the exact step a hurried operator might skip — and that's
  // how the silent-fail bug went unnoticed (Fabrizio 27/06/2026).
  useEffect(() => {
    handlePreview();
  }, []);

  const handlePreview = async () => {
    setPreviewLoading(true);
    setError(null);
    try {
      const params = country ? { country_filter: country } : {};
      const r = await axios.get(`${API}/admin/wallet/refresh-targets`, { params });
      setPreview(r.data);
    } catch (e) {
      setError(extractErrorMessage(e, "Errore anteprima"));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Derived: should we surface the Apple credentials warning?
  // Only relevant when the user is actually going to push to Apple
  // (platform = "apple" or "both"). For Google-only refresh the .p12
  // is irrelevant.
  const showAppleCredWarning =
    preview?.apple_credentials &&
    !preview.apple_credentials.ready &&
    (platform === "apple" || platform === "both");
  const showAppleCredLegacy =
    preview?.apple_credentials?.ready &&
    preview.apple_credentials.source === "disk_only" &&
    (platform === "apple" || platform === "both");

  const handleRun = async () => {
    if (!reason || reason.trim().length < 3) {
      setError("Indica un motivo (min 3 caratteri) per l'audit log.");
      return;
    }
    const blastApple = preview?.apple_target_count ?? "?";
    const blastGoogle = preview?.google_target_count ?? "?";
    const platformLabel = platform === "both" ? "Apple + Google"
      : (platform === "apple" ? "Apple" : "Google");

    // Pre-flight warning included in the confirm dialog so the operator
    // sees the credential issue even if they skipped scrolling to the
    // warning banner above (Fabrizio CR 27/06/2026).
    const credWarning = showAppleCredWarning
      ? `\n\n⚠️ ATTENZIONE: ${preview.apple_credentials.warning}\n` +
        `   I push verso ${blastApple} device iPhone FALLIRANNO.\n`
      : "";

    const confirmMsg = (
      `Stai per inviare un push silenzioso a:\n\n` +
      `  📱 Apple: ${platform === "google" ? "(saltato)" : blastApple} device\n` +
      `  🤖 Google: ${platform === "apple" ? "(saltato)" : blastGoogle} customer\n\n` +
      `Piattaforma: ${platformLabel}\n` +
      `Paese: ${country || "TUTTI"}\n` +
      `Notifica push Android: ${notifyPush ? "✅ ATTIVA" : "❌ Silenziosa"}\n` +
      `Motivo: ${reason}` +
      credWarning +
      `\nConfermi?`
    );
    if (!window.confirm(confirmMsg)) return;

    setRunning(true);
    setError(null);
    setReport(null);
    try {
      const body = {
        reason: reason.trim(),
        platform,
        batch_sleep_ms: batchSleepMs,
        notify_push: notifyPush,
        ...(country ? { country_filter: country } : {}),
      };
      const r = await axios.post(`${API}/admin/wallet/mass-refresh`, body);
      setReport(r.data);
    } catch (e) {
      setError(extractErrorMessage(e, "Errore mass-refresh"));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="bg-[#0F0F0F] border border-gray-800 rounded-2xl p-6"
      data-testid="wallet-mass-refresh-widget"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <RadioTower className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">
              Aggiorna tutte le card Wallet
            </h3>
            <p className="text-gray-500 text-xs">
              Push manuale alle card già salvate dai clienti — utile dopo un cambio del template (nuovi backField, traduzioni)
            </p>
          </div>
        </div>
      </div>

      {/* Pre-flight Apple credentials warning (Fabrizio CR 27/06/2026).
          Visible BEFORE the operator launches the broadcast so they're
          warned of the silent-fail scenario where every iPhone push
          returns FileNotFoundError because the .p12 is missing. */}
      {showAppleCredWarning && (
        <div
          className="mb-4 flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/40 rounded-xl"
          data-testid="wmr-apple-cred-missing"
        >
          <ShieldAlert className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-red-300 font-semibold text-sm mb-1">
              Apple Wallet · Certificato mancante
            </p>
            <p className="text-red-200/80 text-xs leading-relaxed mb-2">
              {preview.apple_credentials.warning}
            </p>
            <p className="text-red-200/60 text-[11px] leading-relaxed">
              Target Apple impattati: <strong>{preview.apple_target_count}</strong> device.
              Suggerimento: cambia piattaforma a <em>&quot;Solo Google&quot;</em> oppure carica
              il <code className="px-1 py-0.5 rounded bg-black/30">.p12</code> prima di procedere.
            </p>
          </div>
        </div>
      )}
      {showAppleCredLegacy && (
        <div
          className="mb-4 flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl"
          data-testid="wmr-apple-cred-legacy"
        >
          <ShieldAlert className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
          <p className="text-amber-200/90 text-xs leading-relaxed">
            {preview.apple_credentials.warning}
          </p>
        </div>
      )}
      {preview?.apple_credentials?.ready &&
        preview.apple_credentials.source === "mongodb" &&
        (platform === "apple" || platform === "both") && (
        <div
          className="mb-4 flex items-center gap-2 p-2 bg-emerald-500/5 border border-emerald-500/20 rounded-lg"
          data-testid="wmr-apple-cred-ok"
        >
          <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <p className="text-emerald-300/80 text-[11px]">
            Apple Wallet · Certificato OK (MongoDB) · push pronti per <strong>{preview.apple_target_count}</strong> device
          </p>
        </div>
      )}

      {/* Form controls */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
            Piattaforma
          </label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="w-full bg-[#1A1A1A] text-gray-200 text-sm border border-gray-700 rounded-lg px-2 py-2 cursor-pointer focus:border-lojack-red outline-none"
            data-testid="wmr-platform"
          >
            <option value="both">Apple + Google</option>
            <option value="apple">Solo Apple</option>
            <option value="google">Solo Google</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
            Paese
          </label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full bg-[#1A1A1A] text-gray-200 text-sm border border-gray-700 rounded-lg px-2 py-2 cursor-pointer focus:border-lojack-red outline-none"
            data-testid="wmr-country"
          >
            {COUNTRIES.map((c) => (
              <option key={c || "ALL"} value={c}>
                {c || "Tutti i paesi"}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
            Pausa tra push (ms)
          </label>
          <input
            type="number" min={0} max={2000} step={10}
            value={batchSleepMs}
            onChange={(e) => setBatchSleepMs(Number(e.target.value || 0))}
            className="w-full bg-[#1A1A1A] text-gray-200 text-sm border border-gray-700 rounded-lg px-2 py-2 focus:border-lojack-red outline-none"
            data-testid="wmr-batch-sleep"
          />
        </div>
        <div className="md:col-span-1">
          <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
            Motivo (audit)
          </label>
          <input
            type="text"
            placeholder="es. Deploy SOS + multi-centrale"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full bg-[#1A1A1A] text-gray-200 text-sm border border-gray-700 rounded-lg px-2 py-2 focus:border-lojack-red outline-none"
            data-testid="wmr-reason"
          />
        </div>
      </div>

      {/* Push notify toggle (Fabrizio CR 26/06/2026) — granular per-run
          override of the WALLET_PUSH_NOTIFY env. Apple iOS riceve sempre
          il banner automatico via changeMessage; questa checkbox controlla
          SOLO la notifica push Android (TEXT_AND_NOTIFY). */}
      <label
        className="flex items-start gap-3 mb-4 p-3 bg-[#141414] border border-gray-800 rounded-lg cursor-pointer hover:bg-[#181818] transition-colors"
        data-testid="wmr-notify-push-label"
      >
        <input
          type="checkbox"
          checked={notifyPush}
          onChange={(e) => setNotifyPush(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-amber-500 cursor-pointer"
          data-testid="wmr-notify-push"
        />
        <div className="flex-1 min-w-0">
          <p className="text-gray-200 text-xs font-semibold">
            📢 Invia notifica push ai clienti
          </p>
          <p className="text-gray-500 text-[11px] mt-0.5 leading-relaxed">
            {notifyPush ? (
              <>
                <span className="text-amber-300">ATTIVA</span> — ogni device Android riceverà una notifica push <strong>&quot;LoJack SecureWallet — Card aggiornata&quot;</strong>.
                iOS riceve sempre un banner automatico Apple-style.
              </>
            ) : (
              <>
                <span className="text-gray-400">Silenziosa</span> — il layout della card viene aggiornato senza notifica push.
                Usa per test interni o refresh tecnici.
              </>
            )}
          </p>
        </div>
      </label>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewLoading}
          className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#222] border border-gray-700 text-gray-200 px-4 py-2 rounded-lg text-sm disabled:opacity-50"
          data-testid="wmr-preview-btn"
        >
          {previewLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          Anteprima target
        </button>
        <button
          type="button"
          onClick={handleRun}
          disabled={running || !reason.trim()}
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="wmr-run-btn"
        >
          {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RadioTower className="w-4 h-4" />}
          Avvia mass-refresh
        </button>
        {!reason.trim() && (
          <span className="text-[11px] text-gray-500" data-testid="wmr-reason-hint">
            Inserisci un motivo per abilitare il broadcast
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div
          className="mb-4 flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"
          data-testid="wmr-error"
        >
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-300 text-xs leading-relaxed">{error}</p>
        </div>
      )}

      {/* Preview panel */}
      {preview && !report && (
        <div
          className="mb-4 bg-[#141414] border border-gray-800 rounded-xl p-4"
          data-testid="wmr-preview-panel"
        >
          <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-3">
            Anteprima blast radius
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <AppleIcon className="w-4 h-4 text-gray-300" />
                <span className="text-[10px] uppercase tracking-wider text-gray-500">Apple Wallet</span>
              </div>
              <p className="text-2xl font-bold text-white" data-testid="wmr-preview-apple-count">
                {preview.apple_target_count}
              </p>
              <p className="text-[11px] text-gray-500">device APN registrati</p>
            </div>
            <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4 text-blue-300" />
                <span className="text-[10px] uppercase tracking-wider text-gray-500">Google Wallet</span>
              </div>
              <p className="text-2xl font-bold text-white" data-testid="wmr-preview-google-count">
                {preview.google_target_count}
              </p>
              <p className="text-[11px] text-gray-500">customer con card salvata</p>
            </div>
          </div>
          {(preview.apple_by_country?.length > 0 || preview.google_by_country?.length > 0) && (
            <div className="text-[11px] text-gray-400 leading-relaxed">
              <p className="mb-1"><strong>Apple per nazione</strong>:{" "}
                {preview.apple_by_country.map((b) =>
                  `${b.country}: ${b.count}`
                ).join(" · ") || "—"}
              </p>
              <p><strong>Google per nazione</strong>:{" "}
                {preview.google_by_country.map((b) =>
                  `${b.country}: ${b.count}`
                ).join(" · ") || "—"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Run report */}
      {report && (
        <div
          className="mb-4 bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-4"
          data-testid="wmr-report-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-300 font-semibold text-sm">
              Mass-refresh completato in {report.duration_sec}s
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-300">
            <div className="bg-[#141414] border border-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <AppleIcon className="w-4 h-4" />
                <span className="font-semibold">Apple Wallet</span>
                {report.apple.ran ? null : (
                  <span className="text-[10px] text-gray-500">(saltato)</span>
                )}
              </div>
              <p>Target: <span className="font-mono text-white">{report.apple.devices_total}</span></p>
              <p>Push OK: <span className="font-mono text-emerald-300">{report.apple.devices_pushed_ok}</span></p>
              <p>Push falliti: <span className="font-mono text-red-300">{report.apple.devices_pushed_failed}</span></p>
              <p>Skipped (inattivi): <span className="font-mono text-gray-400">{report.apple.devices_skipped_inactive}</span></p>
              {report.apple.fail_reasons?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-800" data-testid="wmr-apple-fail-reasons">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                    Dettaglio fallimenti
                  </p>
                  {report.apple.fail_reasons.map((r) => (
                    <p key={r.reason} className="text-[11px]">
                      <span className="font-mono text-red-300">{r.reason}</span>:{" "}
                      <span className="text-gray-400">{r.count} device</span>
                      {(r.reason === "BadDeviceToken" || r.reason === "Unregistered") && (
                        <span className="text-[10px] text-gray-500 italic">
                          {" "}(card rimossa dal cliente — auto-disattivata)
                        </span>
                      )}
                    </p>
                  ))}
                </div>
              )}
              {report.apple.error && (
                <p className="text-red-300 mt-1 break-all">Errore: {report.apple.error}</p>
              )}
            </div>
            <div className="bg-[#141414] border border-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-4 h-4 text-blue-300" />
                <span className="font-semibold">Google Wallet</span>
                {report.google.ran ? null : (
                  <span className="text-[10px] text-gray-500">(saltato)</span>
                )}
              </div>
              <p>Customer target: <span className="font-mono text-white">{report.google.customers_total}</span></p>
              <p>PATCH OK: <span className="font-mono text-emerald-300">{report.google.patched_ok}</span></p>
              <p>Skipped/falliti: <span className="font-mono text-gray-400">{report.google.skipped_or_failed}</span></p>
              {report.google.error && (
                <p className="text-red-300 mt-1 break-all">Errore: {report.google.error}</p>
              )}
            </div>
          </div>
          <p className="text-[10px] text-gray-500 mt-3">
            Avviato: {fmtDateTime(report.started_at)} · Concluso: {fmtDateTime(report.finished_at)}
            {report.country_filter && (
              <> · Filtro paese: <span className="font-mono">{report.country_filter}</span></>
            )}
          </p>
        </div>
      )}

      {/* Notes / safety */}
      <div className="mt-2 text-[11px] text-gray-500 leading-relaxed flex items-start gap-2">
        <ChevronRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Apple usa silent push via APN — la card iOS si aggiorna entro 1-3 secondi senza azione del cliente. Google esegue una PATCH server-side: il device Android riceve l&apos;update entro pochi minuti dal Google CDN. Le card che il cliente ha rimosso dal wallet sono auto-skippate (BadDeviceToken / 404 Google).
        </span>
      </div>
    </div>
  );
};
